import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as countryCodes from 'country-codes-list';
import type { Dayjs } from 'dayjs';
import type { SelectOption, UseFormAddEmployee, useFormAddEmployeeProps, ValidationErrors } from '../types/hooks';
import { employeeService } from '../services/service';
import type { Alert, ParamsPostEmployee } from '../types/services';
import dayjs from 'dayjs';
import { useRbSystemAlert } from '@scmlatam/web-ui';
import { getUtcOffsetLabel, getStoredTimeZone, getMinRequiredAge, getStoredCountryCode } from '../utils/hooks';
import { usePermisoEmpleado as usePermiso } from '../../../../auth/permisosModulos';

const useFormAddEmployee = ({ rows, reload }: useFormAddEmployeeProps): UseFormAddEmployee => {
    const { t } = useTranslation('workers.form.add_worker');
    const { showAlert, closeAlert } = useRbSystemAlert();
    const canAddEmployee = usePermiso('agregar-empleado');

    const title: string = t('add_worker.title');
    const [openForm, setOpenFormState] = useState<boolean>(false);
    const setOpenForm = (value: boolean) => setOpenFormState(value && canAddEmployee);

    const [firstName, setFirstName] = useState<string>('');
    const [lastName, setLastName] = useState<string>('');

    const [documentTypes, setDocumentTypes] = useState<SelectOption[]>([]);
    const [documentType, setDocumentType] = useState<string>('');

    const [documentNumber, setDocumentNumber] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const [address, setAddress] = useState<string>('');

    const cc = countryCodes.customList('countryCode', '+{countryCallingCode}');

    const [codes, setCodes] = useState<SelectOption[]>(
        Object.keys(cc).map((key) => ({
            value: key,
            label: key,
            sublabel: cc[key],
        })),
    );

    const [code, setCode] = useState<string>(() => getStoredCountryCode());

    const [phoneNumber, setPhoneNumber] = useState<string>('');
    const [birthdate, setBirthdate] = useState<Dayjs | null>(null);

    const [payRules, setPayRules] = useState<SelectOption[]>([]);
    const [payRule, setPayRule] = useState<string>('');

    const [timeZones, setTimeZones] = useState<SelectOption[]>(() =>
        Intl.supportedValuesOf('timeZone').map((tz) => ({
            value: tz,
            label: tz,
            sublabel: getUtcOffsetLabel(tz),
        })),
    );

    const [timeZone, setTimeZone] = useState<string>(() => getStoredTimeZone());

    const [loadingForm, setLoadingForm] = useState<boolean>(false);
    const [loadingAddEmployee, setLoadingAddEmployee] = useState<boolean>(false);
    const [showMessage, setShowMessage] = useState<boolean>(false);

    const [searchDocumentType, setSearchDocumentType] = useState<string>('');
    const [searchPayRule, setSearchPayRule] = useState<string>('');
    const [searchTimeZone, setSearchTimeZone] = useState<string>('');
    const [searchCode, setSearchCode] = useState<string>('');

    const [validationErrors, setValidationErrors] = useState<ValidationErrors>({
        firstName: { error: false, message: '' },
        lastName: { error: false, message: '' },
        documentType: { error: false, message: '' },
        documentNumber: { error: false, message: '' },
        email: { error: false, message: '' },
        address: { error: false, message: '' },
        code: { error: false, message: '' },
        phoneNumber: { error: false, message: '' },
        birthdate: { error: false, message: '' },
        payRule: { error: false, message: '' },
        timeZone: { error: false, message: '' },
    });

    const checkInvalidData = (): boolean => {
        let hasErrors = false;

        if (firstName.trim() === '') {
            setValidationErrors((prev) => ({
                ...prev,
                firstName: {
                    error: true,
                    message: t('add_worker.helper.first_name'),
                },
            }));

            hasErrors = true;
        } else {
            setValidationErrors((prev) => ({
                ...prev,
                firstName: {
                    error: false,
                    message: '',
                },
            }));
        }

        if (lastName.trim() === '') {
            setValidationErrors((prev) => ({
                ...prev,
                lastName: {
                    error: true,
                    message: t('add_worker.helper.last_name'),
                },
            }));
            hasErrors = true;
        } else {
            setValidationErrors((prev) => ({
                ...prev,
                lastName: {
                    error: false,
                    message: '',
                },
            }));
        }

        if (documentType === '') {
            setValidationErrors((prev) => ({
                ...prev,
                documentType: {
                    error: true,
                    message: t('add_worker.helper.document_type'),
                },
            }));
            hasErrors = true;
        } else {
            setValidationErrors((prev) => ({
                ...prev,
                documentType: {
                    error: false,
                    message: '',
                },
            }));
        }

        // Si esta vacio, mostrar mensaje de error
        if (documentNumber.trim() === '') {
            setValidationErrors((prev) => ({
                ...prev,
                documentNumber: {
                    error: true,
                    message: t('add_worker.helper.document_number_1'),
                },
            }));
            hasErrors = true;
            // Si no cumple con el regex del tipo de documento, mostrar mensaje de error
        } else if (!new RegExp(documentTypes.find((dt) => dt.value === documentType)?.regex || '').test(documentNumber)) {
            setValidationErrors((prev) => ({
                ...prev,
                documentNumber: {
                    error: true,
                    message: t('add_worker.helper.document_number_2', {
                        regex: documentTypes.find((dt) => dt.value === documentType)?.regex,
                    }),
                },
            }));
            hasErrors = true;
            // Si ya existe un trabajador con el mismo tipo y numero de documento, mostrar mensaje de error
        } else if (rows.some((row) => row.person_number === documentNumber)) {
            setValidationErrors((prev) => ({
                ...prev,
                documentNumber: {
                    error: true,
                    message: t('add_worker.helper.document_number_3'),
                },
            }));
            hasErrors = true;
        } else {
            setValidationErrors((prev) => ({
                ...prev,
                documentNumber: {
                    error: false,
                    message: '',
                },
            }));
        }

        // Si esta vacio, mostrar mensaje de error
        if (email.trim() === '') {
            setValidationErrors((prev) => ({
                ...prev,
                email: { error: true, message: t('add_worker.helper.email_1') },
            }));
            hasErrors = true;
            // Si no cumple con el regex de email, mostrar mensaje de error
        } else if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email)) {
            setValidationErrors((prev) => ({
                ...prev,
                email: { error: true, message: t('add_worker.helper.email_2') },
            }));
            hasErrors = true;
        } else {
            setValidationErrors((prev) => ({
                ...prev,
                email: {
                    error: false,
                    message: '',
                },
            }));
        }

        if (address.trim() === '') {
            setValidationErrors((prev) => ({
                ...prev,
                address: { error: true, message: t('add_worker.helper.address') },
            }));
            hasErrors = true;
        } else {
            setValidationErrors((prev) => ({
                ...prev,
                address: {
                    error: false,
                    message: '',
                },
            }));
        }

        if (code.trim() === '') {
            setValidationErrors((prev) => ({
                ...prev,
                code: { error: true, message: t('add_worker.helper.country_code') },
            }));
            hasErrors = true;
        } else {
            setValidationErrors((prev) => ({
                ...prev,
                code: {
                    error: false,
                    message: '',
                },
            }));
        }

        if (phoneNumber.trim() === '') {
            setValidationErrors((prev) => ({
                ...prev,
                phoneNumber: { error: true, message: t('add_worker.helper.phone_1') },
            }));
            hasErrors = true;
        } else if (!/^9\d{4}\d{4}$/.test(phoneNumber)) {
            setValidationErrors((prev) => ({
                ...prev,
                phoneNumber: {
                    error: true,
                    message: t('add_worker.helper.phone_2', { regex: '^9\\d{4}\\d{4}$' }),
                },
            }));
            hasErrors = true;
        } else {
            setValidationErrors((prev) => ({
                ...prev,
                phoneNumber: {
                    error: false,
                    message: '',
                },
            }));
        }

        if (birthdate === null) {
            setValidationErrors((prev) => ({
                ...prev,
                birthdate: {
                    error: true,
                    message: t('add_worker.helper.birthdate'),
                },
            }));
            hasErrors = true;
        } else if (dayjs().diff(birthdate, 'year') < getMinRequiredAge()) {
            setValidationErrors((prev) => ({
                ...prev,
                birthdate: {
                    error: true,
                    message: t('add_worker.helper.age_min'),
                },
            }));
            hasErrors = true;
        } else {
            setValidationErrors((prev) => ({
                ...prev,
                birthdate: {
                    error: false,
                    message: '',
                },
            }));
        }

        if (payRule === '') {
            setValidationErrors((prev) => ({
                ...prev,
                payRule: { error: true, message: t('add_worker.helper.pay_rule') },
            }));
            hasErrors = true;
        } else {
            setValidationErrors((prev) => ({
                ...prev,
                payRule: {
                    error: false,
                    message: '',
                },
            }));
        }

        if (timeZone === '') {
            setValidationErrors((prev) => ({
                ...prev,
                timeZone: { error: true, message: t('add_worker.helper.timezone') },
            }));
            hasErrors = true;
        } else {
            setValidationErrors((prev) => ({
                ...prev,
                timeZone: {
                    error: false,
                    message: '',
                },
            }));
        }

        return hasErrors;
    };

    //funcion para verificar cambios en los valores iniciales del formulario
    const hasChanges = (): boolean => {
        return (
            firstName !== '' ||
            lastName !== '' ||
            documentType !== '' ||
            documentNumber !== '' ||
            email !== '' ||
            address !== '' ||
            code !== '' ||
            phoneNumber !== '' ||
            birthdate !== null ||
            payRule !== '' ||
            timeZone !== ''
        );
    };

    const getData = async () => {
        setLoadingForm(true);

        const { documentTypes: dts } = await employeeService.getDocumentTypes();
        setDocumentTypes(dts);

        const { payrules: prs } = await employeeService.getPayrules();
        setPayRules(prs);

        setLoadingForm(false);
    };

    // agregar trabajador
    const addEmployee = async () => {
        if (!canAddEmployee) return;

        const params: ParamsPostEmployee = {
            setLoading: setLoadingAddEmployee,
            body: {
                first_name: firstName,
                last_name: lastName,
                birth_date: birthdate?.format('YYYY-MM-DD') || '',
                email: email,
                phone_number: code + phoneNumber,
                address: address,
                document_type_id: parseInt(documentType),
                document_employee: documentNumber,
                pay_period_end: dayjs().add(-1, 'days').format('YYYY-MM-DD'),
                pay_rule_id: parseInt(payRule),
                timezone: timeZone,
            },
            closing: () => {
                setOpenForm(false);
            },
            reloading: () => {},
        };

        const alert: Alert = {
            t: t,
            showAlert: (severity, message) => String(showAlert(severity, message)),
            closeAlert: closeAlert,
        };

        employeeService.addEmployee({ params, alert }).then(() => {
            reload();
        });
    };

    useEffect(() => {
        if (openForm) {
            getData();
        } else {
            setFirstName('');
            setLastName('');
            setDocumentType('');
            setDocumentNumber('');
            setEmail('');
            setAddress('');
            setCode(getStoredCountryCode());
            setPhoneNumber('');
            setBirthdate(null);
            setPayRule('');
            setTimeZone(getStoredTimeZone());
            setValidationErrors({
                firstName: { error: false, message: '' },
                lastName: { error: false, message: '' },
                documentType: { error: false, message: '' },
                documentNumber: { error: false, message: '' },
                email: { error: false, message: '' },
                address: { error: false, message: '' },
                code: { error: false, message: '' },
                phoneNumber: { error: false, message: '' },
                birthdate: { error: false, message: '' },
                payRule: { error: false, message: '' },
                timeZone: { error: false, message: '' },
            });
        }
    }, [openForm]);

    return {
        t,
        title,
        canAddEmployee,
        openForm,
        setOpenForm,
        firstName,
        setFirstName,
        lastName,
        setLastName,
        documentTypes,
        setDocumentTypes,
        documentType,
        setDocumentType,
        documentNumber,
        setDocumentNumber,
        email,
        setEmail,
        address,
        setAddress,
        codes,
        setCodes,
        code,
        setCode,
        phoneNumber,
        setPhoneNumber,
        birthdate,
        setBirthdate,
        payRules,
        setPayRules,
        payRule,
        setPayRule,
        timeZones,
        setTimeZones,
        timeZone,
        setTimeZone,
        validationErrors,
        setValidationErrors,
        loadingForm,
        setLoadingForm,
        loadingAddEmployee,
        setLoadingAddEmployee,
        searchDocumentType,
        setSearchDocumentType,
        searchPayRule,
        setSearchPayRule,
        searchTimeZone,
        setSearchTimeZone,
        searchCode,
        setSearchCode,
        checkInvalidData,
        hasChanges,
        showMessage,
        setShowMessage,
        addEmployee,
    };
};

export { useFormAddEmployee };
